import { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { BellRing, Printer, FileSpreadsheet, Loader2 } from "lucide-react";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import { api, rp, errMsg } from "@/api";

export function recapReminderLink(r, store, label) {
  const wa = (store?.whatsapp_number || "").replace(/\D/g, "");
  const text = `Halo ${r.name},\n\nIni pengingat tagihan dari *${store?.store_name || "toko"}*${label && label !== "Semua" ? ` (periode ${label})` : ""}:\n- ${r.unpaid_count} pesanan belum bayar\n- *Total tagihan: ${rp(r.unpaid_total)}*\n\nMohon segera dilunasi.${wa ? ` Konfirmasi pembayaran ke WA ${wa}.` : ""} Terima kasih 🙏`;
  const phone = (r.phone || "").replace(/\D/g, "");
  return phone ? `https://wa.me/${phone}?text=${encodeURIComponent(text)}` : `https://wa.me/?text=${encodeURIComponent(text)}`;
}

export function ResellerRecap({ storeId, range, refreshKey, store }) {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const params = { ...(storeId ? { store_id: storeId } : {}), ...(range?.date_from ? { date_from: range.date_from, date_to: range.date_to } : {}) };
    api.get("/admin/report/resellers", { params }).then((r) => setRows(r.data));
  }, [storeId, refreshKey, range?.date_from, range?.date_to]);
  if (!rows) return <p className="empty-note">Memuat...</p>;
  const totUnpaid = rows.reduce((s, r) => s + r.unpaid_total, 0);
  const canPrintExport = store?.access_rights?.print_export_laporan !== false;
  const periode = range?.label || "Semua";

  const printRecap = () => {
    document.body.classList.add("print-recap");
    window.print();
    setTimeout(() => document.body.classList.remove("print-recap"), 800);
  };

  const exportRecap = () => {
    setBusy(true);
    try {
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
        ["Rekap Per Reseller", store?.store_name || "Toko"], ["Periode", periode], ["Diekspor", new Date().toLocaleString("id-ID")], [],
        ["Reseller", "No WA", "Pesanan", "Total Pembelian", "Lunas", "Belum Bayar", "Order Terakhir"],
        ...rows.map((r) => [r.name, r.phone || "", r.order_count, r.total, r.paid_total, r.unpaid_total, r.last_order_at ? new Date(r.last_order_at).toLocaleDateString("id-ID") : "-"]),
        [],
        ["TOTAL", "", rows.reduce((s, r) => s + r.order_count, 0), rows.reduce((s, r) => s + r.total, 0), rows.reduce((s, r) => s + r.paid_total, 0), totUnpaid, ""],
      ]), "Rekap Reseller");
      XLSX.writeFile(wb, `rekap-reseller-${(store?.store_name || "toko").toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${periode.toLowerCase().replace(/[^a-z0-9]+/g, "-")}.xlsx`);
      toast.success("File Excel rekap reseller berhasil di-download");
    } catch (e) { toast.error(errMsg(e, "Gagal export Excel")); } finally { setBusy(false); }
  };

  return (
    <div className="recap-area" data-testid="reseller-recap">
      <div className="print-only print-header">
        <h2 style={{ margin: 0 }}>{store?.store_name || "Toko"}</h2>
        <p className="muted-sm">Rekap Per Reseller periode {periode} · dicetak {new Date().toLocaleString("id-ID")}</p>
      </div>
      <div className="panel-toolbar no-print">
        <h3 className="section-title" style={{ margin: 0 }}>Rekap Per Reseller <span className="muted-sm">({periode}) · total tagihan {rp(totUnpaid)}</span></h3>
        <div className="toolbar-right">
          <button className="btn-ghost sm" onClick={printRecap} disabled={!canPrintExport} title={!canPrintExport ? "Dinonaktifkan superadmin" : "Print rekap reseller"} data-testid="recap-print-btn"><Printer size={14} /> Print</button>
          <button className="btn-primary sm" onClick={exportRecap} disabled={!canPrintExport || busy} title={!canPrintExport ? "Dinonaktifkan superadmin" : "Download Excel rekap"} data-testid="recap-export-btn">{busy ? <Loader2 className="spin" size={14} /> : <FileSpreadsheet size={14} />} Export Excel</button>
        </div>
      </div>
      {rows.length === 0 ? <p className="empty-note" data-testid="recap-empty">Belum ada pesanan pada periode ini.</p> : (
        <div className="table-scroll">
          <table className="report-table" data-testid="recap-table">
            <thead><tr><th>Reseller</th><th>Pesanan</th><th>Total Pembelian</th><th>Lunas</th><th>Belum Bayar</th><th /></tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.reseller_id || r.name} data-testid={`recap-row-${i}`} className={r.unpaid_total > 0 ? "has-unpaid" : ""}>
                  <td><strong>{r.name}</strong><div className="muted-sm">{r.phone ? `WA ${r.phone}` : (r.reseller_id ? "tanpa no. WA" : "tanpa akun reseller")}{r.last_order_at ? ` · terakhir ${new Date(r.last_order_at).toLocaleDateString("id-ID")}` : ""}</div></td>
                  <td>{r.order_count}</td>
                  <td>{rp(r.total)}</td>
                  <td className="profit-text">{rp(r.paid_total)}</td>
                  <td className="warn-text" data-testid={`recap-unpaid-${i}`}>{rp(r.unpaid_total)}{r.unpaid_count > 0 && <div className="muted-sm">{r.unpaid_count} pesanan</div>}</td>
                  <td>{r.unpaid_total > 0 && (store?.access_rights?.edit !== false
                    ? <a className="wa-btn" href={recapReminderLink(r, store, range?.label)} target="_blank" rel="noreferrer" data-testid={`recap-remind-${i}`}><BellRing size={13} /> Tagih WA</a>
                    : <span className="wa-btn btn-off" title="Dinonaktifkan superadmin" data-testid={`recap-remind-${i}`}><BellRing size={13} /> Tagih WA</span>)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const COLORS = ["#16a34a", "#2563eb", "#f59e0b", "#db2777", "#7c3aed", "#dc2626", "#0891b2", "#059669", "#4f46e5"];

const TYPE_BADGE = {
  normal: ["#475569", "#f1f5f9"],
  agen: ["#1e40af", "#dbeafe"],
  reseller: ["#166534", "#dcfce7"],
  semi_reseller: ["#6d28d9", "#ede9fe"],
  konsinyasi: ["#92400e", "#fef3c7"],
};

// Rekap per jenis Mitra (NORMAL/AGEN/RESELLER/SEMI RESELLER/KONSINYASI) — data dari orders+resellers existing (snapshot mitra_type).
export function PartnerTypeRecap({ storeId, range, refreshKey }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    const params = { ...(storeId ? { store_id: storeId } : {}), ...(range?.date_from ? { date_from: range.date_from, date_to: range.date_to } : {}) };
    api.get("/admin/report/partner-types", { params }).then((r) => setData(r.data)).catch(() => setData({ types: [] }));
  }, [storeId, refreshKey, range?.date_from, range?.date_to]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!data) return <p className="empty-note">Memuat rekap mitra...</p>;
  const periode = range?.label || "Semua";
  return (
    <div style={{ marginTop: 22 }} data-testid="partner-type-recap">
      <h3 className="section-title">Rekap Per Jenis Mitra <span className="muted-sm">({periode})</span></h3>
      <div className="stat-grid">
        {data.types.map((t) => {
          const [color, bg] = TYPE_BADGE[t.type] || TYPE_BADGE.normal;
          return (
            <div key={t.type} className="stat-card" data-testid={`partner-card-${t.type}`} style={{ borderLeft: `4px solid ${color}`, alignItems: "flex-start" }}>
              <span className="stat-label"><span style={{ background: bg, color, padding: "1px 8px", borderRadius: 999, fontWeight: 800, fontSize: 11 }} data-testid={`partner-badge-${t.type}`}>{t.label}</span></span>
              <div style={{ display: "grid", gap: 3, fontSize: 13, color: "#334155" }}>
                <span>Mitra: <strong data-testid={`partner-count-${t.type}`}>{t.mitra_count}</strong> · Pesanan: <strong data-testid={`partner-orders-${t.type}`}>{t.order_count}</strong></span>
                <span>Voucher terjual: <strong data-testid={`partner-qty-${t.type}`}>{t.voucher_qty}</strong> · Nilai: <strong>{rp(t.total)}</strong></span>
                {t.top_products.length > 0 && <span className="muted-sm" data-testid={`partner-top-product-${t.type}`}>Terlaris: {t.top_products[0].name} ({t.top_products[0].qty})</span>}
                {t.top_mitra.length > 0 && <span className="muted-sm" data-testid={`partner-top-mitra-${t.type}`}>Pesanan terbanyak: {t.top_mitra[0].name} ({t.top_mitra[0].voucher_qty} voucher)</span>}
                {t.order_count === 0 && <span className="muted-sm">Belum ada transaksi pada periode ini.</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function StatCard({ label, value, testId, accent }) {
  return (
    <div className={`stat-card ${accent || ""}`} data-testid={testId}>
      <span className="stat-label">{label}</span>
      <span className="stat-value">{value}</span>
    </div>
  );
}

export function ReportPanel({ storeId, refreshKey, range, store }) {
  const [rep, setRep] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const params = { ...(storeId ? { store_id: storeId } : {}), ...(range?.date_from ? { date_from: range.date_from, date_to: range.date_to } : {}) };
    api.get("/admin/report", { params }).then((r) => setRep(r.data));
  }, [storeId, refreshKey, range?.date_from, range?.date_to]);

  if (!rep) return <p className="empty-note">Memuat...</p>;

  const rights = store?.access_rights || {};
  const canPrintExport = rights.print_export_laporan !== false;
  const periode = range?.label || "Semua";

  const exportExcel = async () => {
    setBusy(true);
    try {
      const params = { ...(storeId ? { store_id: storeId } : {}), ...(range?.date_from ? { date_from: range.date_from, date_to: range.date_to } : {}) };
      const resellers = (await api.get("/admin/report/resellers", { params })).data;
      const wb = XLSX.utils.book_new();
      const ringkasan = [
        ["Laporan", store?.store_name || "Toko"], ["Periode", periode], ["Diekspor", new Date().toLocaleString("id-ID")], [],
        ["Metrik", "Nilai"],
        ["Jumlah Pesanan", rep.order_count],
        ["Total Pembelian (Harga Beli)", rep.revenue],
        ["Nilai Jual Ulang (Harga Jual)", rep.sell_value],
        ["Selisih Jual - Beli (Laba)", rep.profit],
        ["Sudah Lunas", rep.paid_total],
        [`Belum Bayar (${rep.unpaid_count} pesanan)`, rep.unpaid_total],
      ];
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(ringkasan), "Ringkasan");
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([["Voucher", "Terjual", "Pembelian (Harga Beli)", "Selisih (Laba)"], ...rep.by_voucher.map((v) => [v.name, v.qty, v.revenue, v.profit])]), "Per Voucher");
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([["Reseller", "No WA", "Pesanan", "Total Pembelian", "Lunas", "Belum Bayar", "Order Terakhir"], ...resellers.map((r) => [r.name, r.phone || "", r.order_count, r.total, r.paid_total, r.unpaid_total, r.last_order_at ? new Date(r.last_order_at).toLocaleDateString("id-ID") : "-"])]), "Per Reseller");
      XLSX.writeFile(wb, `laporan-${(store?.store_name || "toko").toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${periode.toLowerCase().replace(/[^a-z0-9]+/g, "-")}.xlsx`);
      toast.success("File Excel berhasil di-download");
    } catch (e) { toast.error(errMsg(e, "Gagal export Excel")); } finally { setBusy(false); }
  };

  return (
    <div className="print-area" data-testid="report-panel">
      <div className="print-only print-header">
        <h2 style={{ margin: 0 }}>{store?.store_name || "Toko"}</h2>
        <p className="muted-sm">Laporan periode {periode} · dicetak {new Date().toLocaleString("id-ID")}</p>
      </div>
      <div className="panel-toolbar no-print">
        <span className="muted-sm">Periode: {periode}</span>
        <div className="toolbar-right">
          <button className="btn-ghost sm" onClick={() => window.print()} disabled={!canPrintExport} title={!canPrintExport ? "Dinonaktifkan superadmin" : "Print laporan"} data-testid="report-print-btn"><Printer size={14} /> Print</button>
          <button className="btn-primary sm" onClick={exportExcel} disabled={!canPrintExport || busy} title={!canPrintExport ? "Dinonaktifkan superadmin" : "Download Excel"} data-testid="report-export-btn">{busy ? <Loader2 className="spin" size={14} /> : <FileSpreadsheet size={14} />} Export Excel</button>
        </div>
      </div>
      <div className="stat-grid">
        <StatCard label="Jumlah Pesanan" value={rep.order_count} testId="report-orders" />
        <StatCard label="Total Pembelian Reseller (Harga Beli)" value={rp(rep.revenue)} testId="report-revenue" />
        <StatCard label="Nilai Jual Ulang (Harga Jual)" value={rp(rep.sell_value)} testId="report-sell-value" />
        <StatCard label="Selisih Jual − Beli" value={rp(rep.profit)} testId="report-profit" accent="accent" />
        <StatCard label="Sudah Lunas" value={rp(rep.paid_total)} testId="report-paid" />
        <StatCard label={`Belum Bayar (${rep.unpaid_count} pesanan)`} value={rp(rep.unpaid_total)} testId="report-unpaid" accent="warn" />
      </div>

      <ResellerRecap storeId={storeId} range={range} refreshKey={refreshKey} store={store} />

      <PartnerTypeRecap storeId={storeId} range={range} refreshKey={refreshKey} />

      <h3 className="section-title" style={{ marginTop: 22 }}>Voucher Paling Laris <span className="muted-sm">({range?.label || "Semua"})</span></h3>
      {rep.by_voucher.length === 0 ? (
        <p className="empty-note" data-testid="report-empty">Belum ada data penjualan voucher.</p>
      ) : (
        <>
          <div className="chart-wrap" data-testid="report-chart">
            <ResponsiveContainer width="100%" height={Math.max(180, rep.by_voucher.length * 44)}>
              <BarChart data={rep.by_voucher} layout="vertical" margin={{ left: 8, right: 24, top: 4, bottom: 4 }}>
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 12 }} />
                <Tooltip formatter={(val, name) => (name === "qty" ? [`${val} pcs`, "Terjual"] : [rp(val), name])} />
                <Bar dataKey="qty" radius={[0, 8, 8, 0]} label={{ position: "right", fontSize: 12 }}>
                  {rep.by_voucher.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <table className="report-table" data-testid="report-table">
            <thead>
              <tr><th>Voucher</th><th>Terjual</th><th>Pembelian</th><th>Selisih</th></tr>
            </thead>
            <tbody>
              {rep.by_voucher.map((s, i) => (
                <tr key={s.name} data-testid={`report-row-${i}`}>
                  <td><span className="dot" style={{ background: COLORS[i % COLORS.length] }} />{s.name}</td>
                  <td>{s.qty}</td>
                  <td>{rp(s.revenue)}</td>
                  <td className="profit-text">{rp(s.profit)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}
